// ink-component
import React from 'react';

import { ThemeProvider } from 'styled-components';
import getGlobalProps from '../../helpers/constants/global-props';
import inferFields from './helpers/inferFields';

import TrackerItem from './TrackerItem/TrackerItem';
import type { Item } from './TrackerItem/TrackerItem';
import { StyledTracker } from './styles';

type MarginSize = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10';

export interface TrackerProps {
  /** List of items in the workflow */
  items: Item[];
  /** The `id` of the active item */
  active: string;
  /** Horizontal version */
  isHorizontal?: boolean;
  /** Margin bottom, in number of lines. **If it's horizontal, it will default to** `2` **instead.** */
  marginBottom?: MarginSize;
  /** Is using React Router */
  withReactRouter?: boolean;
  /** onChange callback. It sends back the item that was clicked the index of that item and the event itself */
  onChange?: (item: Item, index: number, Event: any) => void;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Lists the steps of a workflow
 * @categories navigation
 * @aka progress, stepper
 */
function Tracker(props: TrackerProps) {
  const { items, active, isHorizontal, withReactRouter, onChange } = props;
  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const horizontalContentRef = React.useRef<HTMLDivElement>(null);

  const marginBottom = isHorizontal ? props.marginBottom || '2' : props.marginBottom;

  const handleClick = (item, index, e) => {
    onChange && onChange(item, index, e);
  };

  const itemsWithInferredFields = inferFields(items, active);

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        trackerHorizontal: isHorizontal,
        trackerMarginBottom: marginBottom,
      })}
    >
      <StyledTracker {...globalProps} data-testid={props['data-testid']}>
        {itemsWithInferredFields.map((item, index) => (
          <TrackerItem
            {...item}
            key={index}
            active={active}
            isFirst={index === 0}
            isHorizontal={isHorizontal}
            horizontalContentContainer={horizontalContentRef.current}
            withReactRouter={withReactRouter}
            isLast={index === items.length - 1}
            onClick={e => handleClick(item, index, e)}
          />
        ))}
      </StyledTracker>
      <div ref={horizontalContentRef} />
    </ThemeProvider>
  );
}

Tracker.defaultProps = {
  isHorizontal: false,
  marginBottom: undefined,
  withReactRouter: false,
  onChange: undefined,
};

Tracker.displayName = 'Tracker';

export default Tracker;
